@if (chatbot) {
    <div class="client-container d-flex flex-column">
    <div class="view">
        <div class="chatbot-actions">
            <span class="chatbot-action-hint">Åpner lagret oppsett i en ny fane.</span>
            @if (chatUrl; as url) {
                <a mat-stroked-button [href]="url" target="_blank" rel="noopener noreferrer"
                   aria-label="Åpne chat i ny fane">
                    <mat-icon aria-hidden="true">open_in_new</mat-icon>
                    Åpne chat
                </a>
            } @else {
                <app-disabled-button-reason [disabled]="true"
                                            reason="Velg en chatbot med tilgjengelig chatadresse for å åpne chatten.">
                    <button mat-stroked-button type="button" disabled>
                        <mat-icon aria-hidden="true">open_in_new</mat-icon>
                        Åpne chat
                    </button>
                </app-disabled-button-reason>
            }
        </div>
        <!-- Tabs -->
        <mat-tab-group #matTabGroup (selectedTabChange)="onTabChange($event)" [selectedIndex]="selectedIndex">
            <!-- Non-selectable header tab -->
            <mat-tab [disabled]="true">
                <ng-template mat-tab-label>
                    <div class="tab-header-label">
                        <span class="category"><small>Chatbot</small></span>
                        <span class="object-name">{{ chatbot.name }}</span>
                    </div>
                </ng-template>
                <!-- No content required -->
            </mat-tab>
            <mat-tab label="Overview">
                <div class="tab-content">
                    <app-chatbot-report [metricsData]="reportData"></app-chatbot-report>
                    <app-recent-conversations [chatbotId]="chatbot.id"></app-recent-conversations>
                    <mat-card class="cardWithShadow">
                        <mat-card-content class="p-24">
                            <div class="d-flex w-100 flex-column">
                                <mat-card-title>Summary of conversations last 7 days</mat-card-title>
                                <div class="m-l-auto" style="width:100%">
                                    @if (isSummaryLoading) {
                                        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                                    }
                                    @if (!isSummaryLoading) {
                                        <div [innerHTML]="summary"></div>
                                    }
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>
                </div>
            </mat-tab>
            <!-- Settings Tab -->
            <mat-tab label="Settings">
                <div class="tab-content">
                    <app-client-object-settings [clientSettingsForm]="settingsForm"
                                                [object]="chatbot"></app-client-object-settings>
                    <app-general-settings [settingsForm]="settingsForm"></app-general-settings>
                </div>
            </mat-tab>
            <!-- Widget Tab -->
            <mat-tab label="Widget Launcher">
                <div class="tab-content">
                    <app-widget-settings [chatbotId]="chatbot.id" [clientId]="clientService.client?.id ?? ''"
                                         [cookiesToCollect]="chatbot.cookiesToCollect"
                                         [settingsForm]="settingsForm"
                                         [widgetForm]="widgetForm"
                                         [zIndexControl]="widgetZIndexControl"></app-widget-settings>
                </div>
            </mat-tab>
            <!-- Frontend Tab -->
            <mat-tab label="Chat Window">
                <div class="tab-content">
                    <app-frontend-settings [chatbotId]="chatbot.id" [clientId]="chatbot.clientId" [frontendForm]="frontendForm"
                                           [settingsForm]="settingsForm"
                                           [messagesForm]="messagesForm"
                                           [localizationForm]="localizationForm"
                                           [localizationVariantsForm]="localizationVariantsForm"
                                           [isLocalizing]="isLocalizing"
                                           [validationTargetPath]="validationTargetPath"
                                           [imageCompositionRuntimeReady]="imageCompositionRuntimeReady"
                                           (generateLocalization)="generateLocalization()"
                                           [aiProductCardsEnabled]="aiProductCardsEnabledControl.value === true"></app-frontend-settings>
                </div>
            </mat-tab>
            <mat-tab label="Integrations">
                <div class="tab-content">
                    <mat-card class="cardWithShadow">
                        <mat-card-content class="p-24">
                            <div class="d-flex flex-column gap-16">
                                <mat-slide-toggle [formControl]="hubspotForm.controls['enabled']">
                                    Enable HubSpot sync
                                </mat-slide-toggle>

                                @if (hubspotForm.controls['enabled'].value) {
                                    <mat-form-field appearance="outline">
                                        <mat-label>HubSpot Inbox</mat-label>
                                        <mat-select [formControl]="hubspotForm.controls['inboxId']">
                                            <mat-option value="">Select inbox</mat-option>
                                            @for (inbox of hubspotInboxes; track inbox.id) {
                                                <mat-option [value]="inbox.id">{{ inbox.name }}</mat-option>
                                            }
                                        </mat-select>
                                    </mat-form-field>
                                    @if (hubspotForm.errors?.['inboxRequired']) {
                                        <div class="error-text">Select a HubSpot inbox before enabling sync.</div>
                                    }

                                    <mat-form-field appearance="outline">
                                        <mat-label>Handover policy</mat-label>
                                        <mat-select [formControl]="hubspotForm.controls['handoverPolicy']">
                                            <mat-option value="auto">Auto pause bot when agent replies</mat-option>
                                            <mat-option value="manual">Manual handover</mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-slide-toggle [formControl]="hubspotForm.controls['pauseBotOnHandover']">
                                        Pause bot responses during handover
                                    </mat-slide-toggle>

                                    @if (hubspotSyncError) {
                                        <div class="error-text">{{ hubspotSyncError }}</div>
                                    }

                                }
                            </div>
                        </mat-card-content>
                    </mat-card>
                </div>
            </mat-tab>
            <mat-tab label="Publishing">
                <div class="tab-content publishing-tab">
                    <section class="publishing-panel">
                        <div>
                            <h3>Draft workflow</h3>
                            <p>Save a draft to validate and run the stored chatbot regression suite before publishing.</p>
                        </div>
                        <div class="publishing-actions">
                            <app-disabled-button-reason [disabled]="saveDraftDisabled"
                                                        [reason]="saveDraftDisabledReason">
                                <button mat-raised-button color="primary"
                                        (click)="saveDraft()"
                                        [disabled]="saveDraftDisabled">
                                    {{ isVersionAction ? 'Working...' : 'Save draft & test' }}
                                </button>
                            </app-disabled-button-reason>
                            <app-disabled-button-reason [disabled]="publishDraftDisabled"
                                                        [reason]="publishDraftDisabledReason">
                                <button mat-raised-button color="accent"
                                        (click)="publishDraft()"
                                        [disabled]="publishDraftDisabled">
                                    Publish draft
                                </button>
                            </app-disabled-button-reason>
                        </div>
                    </section>

                    <section class="regression-tests-panel">
                        <div class="panel-heading">
                            <div>
                                <h3>Regression tests</h3>
                                <p>Stored tests run automatically for draft saves, publishes, and rollbacks.</p>
                            </div>
                            <div class="publishing-actions">
                                <app-disabled-button-reason [disabled]="regressionActionDisabled"
                                                            [reason]="regressionActionDisabledReason">
                                    <button mat-button color="primary"
                                            (click)="addRegressionTestCase()"
                                            [disabled]="regressionActionDisabled">
                                        Add test
                                    </button>
                                </app-disabled-button-reason>
                                <app-disabled-button-reason [disabled]="regressionActionDisabled"
                                                            [reason]="regressionActionDisabledReason">
                                    <button mat-raised-button color="primary"
                                            (click)="saveRegressionTests()"
                                            [disabled]="regressionActionDisabled">
                                        {{ isSavingRegressionTests ? 'Saving...' : 'Save tests' }}
                                    </button>
                                </app-disabled-button-reason>
                            </div>
                        </div>

                        @if (regressionTestCases.length === 0) {
                            <p>No regression tests configured.</p>
                        } @else {
                            <div class="regression-tests-list">
                                @for (testCase of regressionTestCases; track $index; let index = $index) {
                                    <div class="regression-test-row">
                                        <mat-checkbox [(ngModel)]="testCase.enabled"
                                                      [name]="'chatbot-regression-enabled-' + index">
                                            Enabled
                                        </mat-checkbox>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Name</mat-label>
                                            <input matInput [(ngModel)]="testCase.name"
                                                   [name]="'chatbot-regression-name-' + index">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Classification contains</mat-label>
                                            <input matInput [(ngModel)]="testCase.expectedClassificationPromptContains"
                                                   [name]="'chatbot-regression-classification-' + index">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Instructions contain</mat-label>
                                            <input matInput [(ngModel)]="testCase.expectedAssistantInstructionsContains"
                                                   [name]="'chatbot-regression-instructions-' + index">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Assistant ref</mat-label>
                                            <input matInput [(ngModel)]="testCase.expectedAssistantRef"
                                                   [name]="'chatbot-regression-assistant-ref-' + index">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Base URL contains</mat-label>
                                            <input matInput [(ngModel)]="testCase.expectedBaseUrlContains"
                                                   [name]="'chatbot-regression-base-url-' + index">
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                            <mat-label>Default locale</mat-label>
                                            <input matInput [(ngModel)]="testCase.expectedDefaultLocale"
                                                   [name]="'chatbot-regression-locale-' + index">
                                        </mat-form-field>
                                        <app-disabled-button-reason [disabled]="regressionActionDisabled"
                                                                    [reason]="regressionActionDisabledReason">
                                            <button mat-icon-button color="warn"
                                                    aria-label="Remove regression test"
                                                    (click)="removeRegressionTestCase(index)"
                                                    [disabled]="regressionActionDisabled">
                                                <mat-icon>delete</mat-icon>
                                            </button>
                                        </app-disabled-button-reason>
                                    </div>
                                }
                            </div>
                        }
                    </section>

                    @if (activeDraft) {
                        <section class="version-status">
                            <div>
                                <span class="status-label">Active draft</span>
                                <strong>{{ activeDraft.id }}</strong>
                            </div>
                            <div>
                                <span class="status-label">Validation</span>
                                <strong [class.status-ok]="activeDraft.validationResult.passed"
                                        [class.status-fail]="!activeDraft.validationResult.passed">
                                    {{ activeDraft.validationResult.passed ? 'Passed' : 'Failed' }}
                                </strong>
                            </div>
                            <div>
                                <span class="status-label">Tests</span>
                                <strong [class.status-ok]="activeDraft.testRunSummary.passed"
                                        [class.status-fail]="!activeDraft.testRunSummary.passed">
                                    {{ activeDraft.testRunSummary.passed ? 'Passed' : 'Failed' }}
                                </strong>
                            </div>
                        </section>

                        @if (activeDraft.validationResult.errors.length) {
                            <div class="version-messages status-fail">
                                @for (error of activeDraft.validationResult.errors; track error) {
                                    <div>{{ error }}</div>
                                }
                            </div>
                        }
                    }

                    <section class="versions-list">
                        <h3>Version history</h3>
                        @if (chatbotVersions.length === 0) {
                            <p>No chatbot versions yet.</p>
                        } @else {
                            @for (version of chatbotVersions; track version.id) {
                                <div class="version-row">
                                    <div>
                                        <strong>{{ version.status }}</strong>
                                        <span>{{ version.createdAt | date:'short' }}</span>
                                        @if (version.publishedAt) {
                                            <span>Published {{ version.publishedAt | date:'short' }}</span>
                                        }
                                    </div>
                                    <app-disabled-button-reason [disabled]="rollbackDisabled(version)"
                                                                [reason]="rollbackDisabledReason(version)">
                                        <button mat-button color="primary"
                                                (click)="rollback(version)"
                                                [disabled]="rollbackDisabled(version)">
                                            Rollback
                                        </button>
                                    </app-disabled-button-reason>
                                </div>
                            }
                        }
                    </section>
                </div>
            </mat-tab>
            <!-- Assistants Tab -->
            <mat-tab label="Assistants">
                <div class="tab-content">
                    <app-listings [multiselect]="true"
                                  [selectedItems]="chatbot.assistantRefs"
                                  (selectionChange)="onAssistantRefsChange($event)"
                                  selectionType="multi"
                                  selectionObjectType="assistants"></app-listings>
                </div>
            </mat-tab>
        </mat-tab-group>
        <!-- Save and Delete Buttons -->
        @if (matTabGroup.selectedIndex !== 1) {
            <div class="action-buttons">
                <app-disabled-button-reason [disabled]="saveDisabled"
                                            [reason]="saveDisabledReason">
                    <button (click)="save()"
                            [disabled]="saveDisabled"
                            color="primary"
                            mat-raised-button>
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </button>
                </app-disabled-button-reason>
                <app-disabled-button-reason [disabled]="deleteDisabled"
                                            [reason]="deleteDisabledReason">
                    <button (click)="delete()" [disabled]="deleteDisabled" color="warn" mat-raised-button>
                        {{ isDeleting ? 'Deleting...' : 'Delete' }}
                    </button>
                </app-disabled-button-reason>
        </div>
        }
    </div>
    </div>
}
